import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from 'react-query'
import { Toaster } from 'react-hot-toast'
import { useEffect, useState, useCallback, useRef } from 'react'
import { X } from 'lucide-react'
import ErrorBoundary from './components/ErrorBoundary'
import Layout from './components/layout/Layout'
import Dashboard from './pages/Dashboard'
import Reservations from './pages/Reservations'
import Tables from './pages/Tables'
import Customers from './pages/Customers'
import Settings from './pages/Settings'
import Login from './pages/Login'
import ReservationsPreview from './pages/ReservationsPreview'
import ProtectedRoute from './components/ProtectedRoute'
import React from 'react'

const FloorPlanV2 = React.lazy(() => import('./pages/FloorPlanV2'))
const TimelinePage = React.lazy(() => import('./pages/Timeline'))

import PWAInstallPrompt from './components/PWAInstallPrompt'
import { DisableBrowserBehaviors } from './components/DisableBrowserBehaviors'
import { DarkModeProvider } from './components/DarkModeProvider'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { TenantProvider, useTenant } from './contexts/TenantContext'
import { NotificationsProvider } from './contexts/NotificationsContext'
import { checkAndClearSessionIfNeeded } from './lib/storageAdapter'
import { initializeSync, pullLatestFromSupabase, resetSupabaseReachability } from './services/supabaseSync'
import { publishCrossTab, type CrossTabEvent } from './services/crossTab'
import './lib/i18n'

import { APP_NAME, MULTI_TENANT_PICKER_ALLOWED_EMAILS } from './lib/config'
import NotificationsPage from './pages/Notifications'

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})

type PwaUpdateAvailableDetail = {
  /** From vite-plugin-pwa `registerSW` — activates waiting worker and reloads the page */
  applyUpdate?: () => void | Promise<void>;
};

function PWAUpdateBanner() {
  const [show, setShow] = useState(false);
  const [applying, setApplying] = useState(false);
  const applyUpdateRef = useRef<(() => void | Promise<void>) | null>(null);
  const dismiss = useCallback(() => setShow(false), []);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<PwaUpdateAvailableDetail>).detail;
      applyUpdateRef.current = typeof detail?.applyUpdate === 'function' ? detail.applyUpdate : null;
      setShow(true);
    };
    window.addEventListener('pwa-update-available', handler);
    return () => window.removeEventListener('pwa-update-available', handler);
  }, []);

  const handleApplyUpdate = useCallback(async () => {
    setApplying(true);
    const apply = applyUpdateRef.current;
    try {
      if (typeof apply === 'function') {
        await Promise.resolve(apply());
      } else {
        window.location.reload();
      }
    } catch {
      window.location.reload();
    } finally {
      setApplying(false);
    }
  }, []);

  useEffect(() => {
    if (!show) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [show, dismiss]);

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[10050] flex items-center justify-center p-4 sm:p-6 pointer-events-auto"
      style={{
        paddingTop: 'max(1rem, env(safe-area-inset-top, 0px))',
        paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 0px))',
        paddingLeft: 'max(1rem, env(safe-area-inset-left, 0px))',
        paddingRight: 'max(1rem, env(safe-area-inset-right, 0px))',
      }}
    >
      {/* Backdrop: blocks interaction with the app; does not dismiss (user must use X or button) */}
      <div className="absolute inset-0 bg-black/60 dark:bg-black/70 backdrop-blur-[2px]" aria-hidden />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pwa-update-dialog-title"
        className="relative z-10 w-full max-w-md rounded-xl border border-border bg-card text-card-foreground shadow-2xl outline-none"
      >
        <button
          type="button"
          onClick={dismiss}
          className="absolute right-3 top-3 rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors touch-target min-h-[44px] min-w-[44px] flex items-center justify-center"
          aria-label="Κλείσιμο"
        >
          <X className="h-5 w-5" strokeWidth={2} />
        </button>

        <div className="px-6 pb-6 pt-14 sm:pt-12">
          <h2 id="pwa-update-dialog-title" className="text-lg font-semibold text-foreground pr-10">
            Νέα έκδοση διαθέσιμη
          </h2>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
            Πατήστε «Κατάλαβα» για να εγκατασταθεί η νέα έκδοση — η εφαρμογή θα ανανεωθεί αυτόματα. Αν δεν
            ανοίξει ξανά μόνη της, κλείστε την εντελώς (από τον διακόπτη εφαρμογών) και ανοίξτε την ξανά.
          </p>
          <button
            type="button"
            onClick={handleApplyUpdate}
            disabled={applying}
            className="mt-6 w-full px-4 py-3 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 touch-target min-h-[44px] transition-opacity disabled:opacity-60 disabled:pointer-events-none"
          >
            {applying ? 'Ενημέρωση…' : 'Κατάλαβα'}
          </button>
        </div>
      </div>
    </div>
  );
}

function App() {
  useEffect(() => {
    // Check and clear session if "Remember Me" is disabled
    checkAndClearSessionIfNeeded();
  }, []);

  return (
    <ErrorBoundary>
      <DarkModeProvider>
        <AuthProvider>
          <TenantProvider>
            <NotificationsProvider>
              <SyncInitializer>
                <QueryClientProvider client={queryClient}>
                  <Router>
                    <div className="app-shell fixed inset-0 bg-background no-edge-swipe overflow-hidden safe-area-top safe-area-left safe-area-right flex flex-col min-h-0">
                      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden no-scrollbar">
                        <Routes>
                          <Route path="/login" element={<Login />} />
                          <Route path="/bookings" element={<ReservationsPreview />} />
                          <Route path="/reservations-preview" element={<Navigate to="/bookings" replace />} />
                          <Route
                            path="/*"
                            element={
                              <ProtectedRoute>
                                <TenantGuard>
                                  <Layout>
                                    <Routes>
                                      <Route path="/" element={<Dashboard />} />
                                      <Route path="/reservations" element={<Reservations />} />
                                      <Route path="/tables" element={<Tables />} />
                                      <Route path="/notifications" element={<NotificationsPage />} />
                                      <Route path="/floor-plan" element={
                                        <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div></div>}>
                                          <FloorPlanV2 />
                                        </React.Suspense>
                                      } />
                                      <Route path="/floor-plan/editor" element={
                                        <Navigate to="/floor-plan" replace />
                                      } />
                                      <Route path="/floor-plan/v2" element={
                                        <Navigate to="/floor-plan" replace />
                                      } />
                                      <Route path="/customers" element={<Customers />} />
                                      <Route path="/settings" element={<Settings />} />
                                      <Route path="/timeline" element={
                                        <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                                          <TimelinePage />
                                        </React.Suspense>
                                      } />
                                    </Routes>
                                  </Layout>
                                </TenantGuard>
                                <PWAInstallPrompt />
                                <DisableBrowserBehaviors />
                              </ProtectedRoute>
                            }
                          />
                        </Routes>
                      </div>
                    <Toaster
                      position="bottom-center"
                      containerStyle={{
                        bottom: 'max(1.5rem, env(safe-area-inset-bottom, 1.5rem))',
                      }}
                      toastOptions={{
                        duration: 4000,
                        style: {
                          background: 'hsl(var(--card))',
                          color: 'hsl(var(--card-foreground))',
                          border: '1px solid hsl(var(--border))',
                        },
                      }}
                    />
                    <PWAUpdateBanner />
                  </div>
                  </Router>
                </QueryClientProvider>
              </SyncInitializer>
            </NotificationsProvider>
          </TenantProvider>
        </AuthProvider>
      </DarkModeProvider>
    </ErrorBoundary>
  )
}

export default App 

// SyncInitializer sets up sync after tenant database is ready
function SyncInitializer({ children }: { children: React.ReactNode }) {
  const { tenantId, dbReady } = useTenant();

  useEffect(() => {
    if (!tenantId || !dbReady) return;

    const initializeAppSync = async () => {
      try {
        console.log(`✅ ${APP_NAME} ready with offline-first sync`);
        // Initialize Supabase sync (resets reachability and pulls latest data)
        await initializeSync();
      } catch (error) {
        console.error('❌ Failed to initialize sync:', error);
      }
    };

    initializeAppSync();
  }, [tenantId, dbReady]);

  useEffect(() => {
    if (!tenantId || !dbReady) return;

    const handleOnline = () => {
      // Reset reachability when coming back online
      resetSupabaseReachability();
      pullLatestFromSupabase();
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [tenantId, dbReady]);

  // Set up periodic polling for cross-device sync
  // Note: Supabase Realtime is in alpha and not available, so we use polling
  useEffect(() => {
    if (!tenantId || !dbReady) return;

    console.log('🔄 Setting up periodic sync polling...');

    // Poll every 10 seconds for near real-time sync across devices
    // This ensures that when User A creates a reservation, User B sees it within 10 seconds
    const pollInterval = setInterval(() => {
      if (navigator.onLine) {
        pullLatestFromSupabase().then((pulled) => {
          if (pulled) {
            // Notify other tabs that data was updated
            publishCrossTab({ type: 'sync:all' as CrossTabEvent });
          }
        });
      }
    }, 10000); // 10 seconds

    return () => {
      console.log('🔄 Cleaning up sync polling...');
      clearInterval(pollInterval);
    };
  }, [tenantId, dbReady]);

  return <>{children}</>;
}

// TenantGuard blocks tenant-dependent UI when the user has no memberships
function TenantGuard({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { tenants, tenantId, loading, dbReady, setActiveTenantId } = useTenant();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center p-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-3" />
          <p className="text-muted-foreground">Loading your workspace…</p>
        </div>
      </div>
    );
  }

  if (!dbReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center p-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-3" />
          <p className="text-muted-foreground">Preparing your workspace…</p>
        </div>
      </div>
    );
  }

  if (!tenants.length) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="max-w-md text-center space-y-4 p-6 rounded-lg border border-border bg-card shadow-sm">
          <h1 className="text-xl font-semibold text-foreground">No restaurant linked</h1>
          <p className="text-muted-foreground">
            Your account is not linked to any restaurant in {APP_NAME}. Please contact the administrator.
          </p>
        </div>
      </div>
    );
  }

  // Tenant is resolved (either auto-selected or chosen by user)
  if (!tenantId && tenants.length > 0) {
    // Only developer/admin accounts may see the restaurant list. Client accounts are
    // assigned to a single restaurant and must never see other clients' restaurants.
    const canPickRestaurant = user?.email && MULTI_TENANT_PICKER_ALLOWED_EMAILS.includes(user.email.toLowerCase());

    if (canPickRestaurant) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background px-6">
          <div className="max-w-md w-full space-y-4 p-6 rounded-lg border border-border bg-card shadow-sm">
            <div className="text-center space-y-1">
              <h1 className="text-xl font-semibold text-foreground">Select a restaurant</h1>
              <p className="text-muted-foreground text-sm">
                Your account is linked to more than one restaurant. Choose one to continue.
              </p>
            </div>
            <ul className="space-y-2">
              {tenants.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => setActiveTenantId(t.id)}
                    className="w-full text-left px-4 py-3 rounded-lg border border-border bg-background hover:bg-muted hover:border-primary/50 transition-colors font-medium text-foreground"
                  >
                    {t.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      );
    }

    // Client with multiple tenants (data error): do not expose other restaurants.
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="max-w-md text-center space-y-4 p-6 rounded-lg border border-border bg-card shadow-sm">
          <h1 className="text-xl font-semibold text-foreground">Contact administrator</h1>
          <p className="text-muted-foreground">
            Your account is linked to more than one restaurant. Please contact the administrator to resolve this.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
